import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Divider,
  FooterHelp,
  InlineStack,
  Link,
  Page,
  Spinner,
  Tabs,
  Text,
  TextField,
} from '@shopify/polaris';
import { Helmet } from 'react-helmet-async';
import { useGetTaxFillOrder, useMutateTaxFillOrderTaxInformation, Typedorder_invoice_info, TypedOrders } from 'queries/orders.query';
import __helpers from 'helpers/index';
import { useSettings } from 'context/SettingsContext';

type TaxFormData = {
  invoice_tax_type: 'PERSONAL' | 'COMPANY';
  name: string;
  buyer_name: string;
  invoice_tax_code: string;
  invoice_tax_cccd: string;
  invoice_tax_mamoiquanhengansach: string;
  invoice_email: string;
  invoice_phone: string;
  invoice_address: string;
};

const TAX_TYPE_TABS = [
  { id: 'PERSONAL', content: 'Thuế cá nhân' },
  { id: 'COMPANY', content: 'Thuế doanh nghiệp' },
];

const STEP_TITLES = ['Kiểm tra mã đơn hàng', 'Điền thông tin thuế', 'Xem lại & lưu', 'Đã lưu thành công'];
const BUSINESS_TAX_LOOKUP_DEBOUNCE_MS = 700;

type BusinessTaxLookupResponse = {
  orgType?: string;
  taxID?: string;
  name?: string | null;
  address?: string | null;
  taxDepartment?: string;
  status?: string;
  updatedAt?: string;
};

export default function TaxFill() {
  const { mutateAsync: saveTaxInformation, isPending: savingTaxInfo } = useMutateTaxFillOrderTaxInformation();

  const [currentStep, setCurrentStep] = useState(1);
  const [orderCodeInput, setOrderCodeInput] = useState('');
  const [orderCode, setOrderCode] = useState('');
  const [ticketError, setTicketError] = useState('');
  const [orderId, setOrderId] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saveSuccessMessage, setSaveSuccessMessage] = useState('');
  const [taxLookupError, setTaxLookupError] = useState('');
  const [taxLookupMessage, setTaxLookupMessage] = useState('');
  const [taxLookupLoading, setTaxLookupLoading] = useState(false);

  const { settings } = useSettings();

  const [formData, setFormData] = useState<TaxFormData>({
    invoice_tax_type: 'COMPANY',
    name: '',
    buyer_name: '',
    invoice_tax_code: '',
    invoice_tax_cccd: '',
    invoice_tax_mamoiquanhengansach: '',
    invoice_email: '',
    invoice_phone: '',
    invoice_address: '',
  });

  const [savedTaxInfo, setSavedTaxInfo] = useState<Typedorder_invoice_info | null>(null);

  const { data: taxFillOrderResponse, isFetching: loadingOrder } = useGetTaxFillOrder(orderCode);

  const [orderData, setOrderData] = useState<TypedOrders>(null);

  useEffect(() => {
    if (taxFillOrderResponse && taxFillOrderResponse?.order_data) {
      setOrderData(taxFillOrderResponse?.order_data);
    }
  }, [taxFillOrderResponse]);

  useEffect(() => {
    if (!orderData) return;

    setFormData({
      invoice_tax_type: (orderData?.order_invoice_info?.tax_type as 'PERSONAL' | 'COMPANY') || 'COMPANY',
      name: orderData?.order_invoice_info?.name || orderData?.customer_data?.display_name || '',
      buyer_name: orderData?.order_invoice_info?.buyer_name || '',
      invoice_tax_code: orderData?.order_invoice_info?.tax_code || '',
      invoice_tax_cccd: orderData?.order_invoice_info?.tax_cccd || '',
      invoice_tax_mamoiquanhengansach: orderData?.order_invoice_info?.tax_mamoiquanhengansach || '',
      invoice_email: orderData?.order_invoice_info?.invoice_email || '',
      invoice_phone: orderData?.order_invoice_info?.invoice_phone || orderData?.customer_data?.user_phonenumber || '',
      invoice_address: orderData?.order_invoice_info?.invoice_address || '',
    });
  }, [orderData]);

  const updateField = useCallback((field: keyof TaxFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const normalizedTaxCode = useMemo(() => formData.invoice_tax_code.trim(), [formData.invoice_tax_code]);

  const validateStep2 = useCallback(() => {
    if (!formData.name.trim()) return 'Vui lòng nhập tên xuất hóa đơn.';
    if (!formData.invoice_phone.trim()) return 'Vui lòng nhập số điện thoại.';

    return '';
  }, [formData]);

  const handleCheckTicket = useCallback(async () => {
    setTicketError('');
    setSaveError('');
    setSaveSuccessMessage('');

    const normalizedOrderCode = orderCodeInput.trim();
    if (!normalizedOrderCode) {
      setTicketError('Bạn chưa nhập mã đơn hàng.');
      return;
    }

    setOrderCode(normalizedOrderCode);
  }, [orderCodeInput]);

  useEffect(() => {
    if (!orderCode) return;
    if (loadingOrder) return;

    if (!taxFillOrderResponse?.valid || !taxFillOrderResponse?.order_data?.id) {
      setTicketError(taxFillOrderResponse?.note || 'Không tìm thấy mã đơn hợp lệ. Vui lòng kiểm tra lại.');
      return;
    }

    setTicketError('');
    setOrderId(String(taxFillOrderResponse.order_data.id));
    setCurrentStep((prevStep) => (prevStep === 1 ? 2 : prevStep));
  }, [loadingOrder, orderCode, taxFillOrderResponse]);

  const handleGoToReview = useCallback(() => {
    const validationError = validateStep2();
    if (validationError) {
      setSaveError(validationError);
      return;
    }
    setSaveError('');
    setCurrentStep(3);
  }, [validateStep2]);

  const OrderSumary = useCallback(() => {
    return (
      <Card>
        <BlockStack gap="200">
          <Text as="p" variant="headingMd">
            Thông tin vé
          </Text>
          <Text as="p" variant="bodyMd">
            Điểm đón: {orderData?.pickup_address}
          </Text>
          <Text as="p" variant="bodyMd">
            Điểm trả: {orderData?.dropoff_address}
          </Text>
          <Text as="p" variant="bodyMd">
            Khởi hành: {orderData ? new Date(Number(orderData.departure_date)).toLocaleString() : ''}
          </Text>
          <Text as="p" variant="bodyMd">
            Số ghế: {orderData?.total_seat}
          </Text>
          <Text as="p" variant="bodyMd">
            Tổng phải trả: {__helpers.formatNumberWithCommas(orderData?.total_must_pay || 0)} VND
          </Text>
        </BlockStack>
      </Card>
    );
  }, [orderData]);

  const handleSave = useCallback(async () => {
    if (!orderId) {
      setSaveError('Thiếu thông tin đơn hàng. Vui lòng kiểm tra lại mã vé.');
      return;
    }

    try {
      setSaveError('');

      await saveTaxInformation({
        order_id: String(orderId),
        tax_type: formData.invoice_tax_type,
        name: formData.name,
        buyer_name: formData.buyer_name,
        tax_code: formData.invoice_tax_code,
        tax_cccd: formData.invoice_tax_cccd,
        tax_mamoiquanhengansach: formData.invoice_tax_mamoiquanhengansach,
        invoice_email: formData.invoice_email,
        invoice_phone: formData.invoice_phone,
        invoice_address: formData.invoice_address,
      });

      setSavedTaxInfo({
        order_id: String(orderId),
        tax_type: formData.invoice_tax_type,
        name: formData.name,
        buyer_name: formData.buyer_name,
        tax_code: formData.invoice_tax_code,
        tax_cccd: formData.invoice_tax_cccd,
        tax_mamoiquanhengansach: formData.invoice_tax_mamoiquanhengansach,
        invoice_email: formData.invoice_email,
        invoice_phone: formData.invoice_phone,
        invoice_address: formData.invoice_address,
      });
      setSaveSuccessMessage('Đã lưu thông tin thuế thành công.');
      setCurrentStep(4);
    } catch (error: any) {
      setSaveError(error?.message || 'Lưu thông tin thuế thất bại, vui lòng thử lại.');
    }
  }, [formData, orderId, saveTaxInformation]);

  const selectedTaxTypeIndex = formData.invoice_tax_type === 'COMPANY' ? 1 : 0;

  const displayTaxInfo = useMemo(() => {
    return (
      savedTaxInfo || {
        tax_type: formData.invoice_tax_type,
        name: formData.name,
        buyer_name: formData.buyer_name,
        tax_code: formData.invoice_tax_code,
        tax_cccd: formData.invoice_tax_cccd,
        tax_mamoiquanhengansach: formData.invoice_tax_mamoiquanhengansach,
        invoice_email: formData.invoice_email,
        invoice_phone: formData.invoice_phone,
        invoice_address: formData.invoice_address,
      }
    );
  }, [formData, savedTaxInfo]);

  useEffect(() => {
    if (formData.invoice_tax_type !== 'COMPANY') {
      setTaxLookupError('');
      setTaxLookupMessage('');
      setTaxLookupLoading(false);
      return;
    }

    if (!normalizedTaxCode) {
      setTaxLookupError('');
      setTaxLookupMessage('');
      setTaxLookupLoading(false);
      return;
    }

    const controller = new AbortController();
    setTaxLookupLoading(true);
    setTaxLookupError('');
    setTaxLookupMessage('');

    const fetchBusinessTaxInformation = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/taxfill/tax-payer/${encodeURIComponent(normalizedTaxCode)}`, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = (await response.json()) as BusinessTaxLookupResponse;
        if (!result?.taxID || !result?.name) {
          throw new Error('Không tìm thấy doanh nghiệp tương ứng với mã số thuế đã nhập.');
        }

        if (controller.signal.aborted) return;

        let companyAddress = result.address?.trim().replace('TP ', 'Thành phố ') || '';
        if (!companyAddress.includes('Việt Nam')) {
          companyAddress = companyAddress + ', Việt Nam';
        }

        setFormData((prev) => {
          if (prev.invoice_tax_type !== 'COMPANY' || prev.invoice_tax_code.trim() !== normalizedTaxCode) return prev;
          return {
            ...prev,
            name: result.name?.trim() || prev.name,
            invoice_address: companyAddress || prev.invoice_address,
          };
        });

        const companyName = result.name?.trim() || 'Doanh nghiệp';
        setTaxLookupMessage(`Đã tự động điền thông tin cho MST ${result.taxID || normalizedTaxCode} (${companyName}). ${result.status || ''}`);
      } catch (error: any) {
        if (controller.signal.aborted) return;
        const normalizedErrorMessage =
          typeof error?.message === 'string' && error.message.trim()
            ? error.message
            : 'Không thể tra cứu mã số thuế lúc này. Vui lòng kiểm tra lại hoặc thử lại sau.';
        setTaxLookupError(`Tra cứu MST thất bại: ${normalizedErrorMessage}`);
      } finally {
        if (!controller.signal.aborted) {
          setTaxLookupLoading(false);
        }
      }
    };

    const timeout = window.setTimeout(fetchBusinessTaxInformation, BUSINESS_TAX_LOOKUP_DEBOUNCE_MS);

    return () => {
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, [formData.invoice_tax_type, normalizedTaxCode]);

  return (
    <Page>
      <Helmet>
        <title>Điền thông tin xuất hóa đơn</title>
      </Helmet>

      <div className="tax-fill-page">
        <style>
          {`
            .tax-fill-page .Polaris-TextField__Input,
            .tax-fill-page .Polaris-Button,
            .tax-fill-page .Polaris-Select__Input,
            .tax-fill-page .Polaris-Label__Text,
            .tax-fill-page .Polaris-Banner__Content {
              font-size: 18px !important;
              line-height: 1.45 !important;
            }

            .tax-fill-page .Polaris-TextField {
              min-height: 52px;
            }
          `}
        </style>

        <Box maxWidth="720px" paddingBlockEnd="800">
          <BlockStack gap="500">
            <BlockStack gap="200">
              <Text as="h1" variant="heading2xl" alignment="center">
                Điền thông tin xuất hóa đơn
              </Text>
              <Text as="p" variant="headingMd" tone="subdued" alignment="center">
                Nếu không có thông tin thuế, chúng tôi sẽ xuất hóa đơn mặc định
              </Text>
            </BlockStack>

            <Card>
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h2" variant="headingLg">
                    Bước {currentStep}/4: {STEP_TITLES[currentStep - 1]}
                  </Text>
                  <Badge tone="info">{`Mã đơn: ${orderCode || `Chưa nhập`} `}</Badge>
                </InlineStack>
                <Divider />

                {currentStep === 1 && (
                  <BlockStack gap="400">
                    <TextField
                      label="Mã đơn hàng"
                      autoComplete="off"
                      placeholder="Ví dụ: MQ4K5U"
                      value={orderCodeInput}
                      onChange={setOrderCodeInput}
                    />

                    {ticketError ? <Banner tone="critical">{ticketError}</Banner> : null}

                    <InlineStack align="end">
                      <Button size="large" variant="primary" loading={loadingOrder} onClick={handleCheckTicket}>
                        Kiểm tra mã đơn
                      </Button>
                    </InlineStack>
                  </BlockStack>
                )}

                {currentStep === 2 && (
                  <BlockStack gap="400">
                    <Text as="p" variant="headingMd">
                      Bước 2: Điền thông tin thuế. Chọn đúng loại hóa đơn phía dưới.
                    </Text>

                    <OrderSumary />

                    {loadingOrder ? (
                      <InlineStack align="center">
                        <Spinner accessibilityLabel="Đang tải đơn hàng" size="large" />
                      </InlineStack>
                    ) : null}

                    <Tabs
                      tabs={TAX_TYPE_TABS}
                      selected={selectedTaxTypeIndex}
                      onSelect={(selectedTab) => updateField('invoice_tax_type', selectedTab === 1 ? 'COMPANY' : 'PERSONAL')}
                    >
                      <Card>
                        <BlockStack gap="300">
                          {formData.invoice_tax_type === 'PERSONAL' ? (
                            <>
                              <TextField
                                label="Số căn cước công dân"
                                autoComplete="off"
                                value={formData.invoice_tax_cccd}
                                requiredIndicator
                                onChange={(value) => updateField('invoice_tax_cccd', value)}
                              />
                              <TextField
                                label="Tên trên CCCD"
                                autoComplete="off"
                                requiredIndicator
                                value={formData.name}
                                onChange={(value) => updateField('name', value)}
                              />
                            </>
                          ) : (
                            <>
                              <TextField
                                label="Mã số thuế doanh nghiệp"
                                autoComplete="off"
                                requiredIndicator
                                value={formData.invoice_tax_code}
                                onChange={(value) => updateField('invoice_tax_code', value)}
                                helpText={
                                  taxLookupLoading
                                    ? 'Đang tra cứu thông tin doanh nghiệp...'
                                    : 'Hệ thống sẽ tự động điền tên công ty và địa chỉ theo mã số thuế.'
                                }
                              />
                              {!taxLookupError && taxLookupMessage ? <Banner tone="success">{taxLookupMessage}</Banner> : null}

                              <TextField
                                label="Mã quan hệ ngân sách"
                                autoComplete="off"
                                value={formData.invoice_tax_mamoiquanhengansach}
                                onChange={(value) => updateField('invoice_tax_mamoiquanhengansach', value)}
                              />

                              <TextField
                                label="Tên công ty"
                                autoComplete="off"
                                value={formData.name}
                                requiredIndicator
                                onChange={(value) => updateField('name', value)}
                              />
                              <TextField
                                label="Tên người mua"
                                autoComplete="off"
                                value={formData.buyer_name}
                                onChange={(value) => updateField('buyer_name', value)}
                              />
                            </>
                          )}

                          <TextField
                            label="Địa chỉ xuất hóa đơn"
                            autoComplete="off"
                            requiredIndicator
                            value={formData.invoice_address}
                            onChange={(value) => updateField('invoice_address', value)}
                          />
                          <TextField
                            label="Email nhận hóa đơn"
                            autoComplete="off"
                            requiredIndicator
                            value={formData.invoice_email}
                            onChange={(value) => updateField('invoice_email', value)}
                          />
                          <TextField
                            label="Số điện thoại"
                            autoComplete="off"
                            requiredIndicator
                            value={formData.invoice_phone}
                            onChange={(value) => updateField('invoice_phone', value)}
                          />
                        </BlockStack>
                      </Card>
                    </Tabs>

                    {taxLookupError ? <Banner tone="critical">{taxLookupError}</Banner> : null}
                    {saveError ? <Banner tone="critical">{saveError}</Banner> : null}

                    <InlineStack align="space-between">
                      <Button size="large" onClick={() => setCurrentStep(1)}>
                        Quay lại
                      </Button>
                      <Button size="large" variant="primary" onClick={handleGoToReview}>
                        Tiếp theo
                      </Button>
                    </InlineStack>
                  </BlockStack>
                )}

                {currentStep === 3 && (
                  <BlockStack gap="400">
                    <Text as="p" variant="headingMd">
                      Bước 3: Kiểm tra lại thông tin một lần cuối trước khi lưu.
                    </Text>
                    <OrderSumary />

                    <Card>
                      <BlockStack gap="200">
                        <Text as="p" variant="headingMd">
                          Loại thuế: {displayTaxInfo.tax_type === 'COMPANY' ? 'Doanh nghiệp' : 'Cá nhân'}
                        </Text>
                        <Text as="p" variant="headingMd">
                          Tên xuất hóa đơn: {displayTaxInfo.name || '—'}
                        </Text>
                        {displayTaxInfo.tax_type === 'COMPANY' ? (
                          <Text as="p" variant="headingMd">
                            Mã số thuế: {displayTaxInfo.tax_code || '—'}
                          </Text>
                        ) : (
                          <Text as="p" variant="headingMd">
                            Số CCCD: {displayTaxInfo.tax_cccd || '—'}
                          </Text>
                        )}
                        <Text as="p" variant="headingMd">
                          Mã quan hệ ngân sách: {displayTaxInfo.tax_mamoiquanhengansach || '—'}
                        </Text>
                        <Text as="p" variant="headingMd">
                          Địa chỉ: {displayTaxInfo.invoice_address || '—'}
                        </Text>
                        <Text as="p" variant="headingMd">
                          Email: {displayTaxInfo.invoice_email || '—'}
                        </Text>
                        <Text as="p" variant="headingMd">
                          Số điện thoại: {displayTaxInfo.invoice_phone || '—'}
                        </Text>
                      </BlockStack>
                    </Card>

                    {saveError ? <Banner tone="critical">{saveError}</Banner> : null}

                    <InlineStack align="space-between">
                      <Button size="large" onClick={() => setCurrentStep(2)}>
                        Sửa lại thông tin
                      </Button>
                      <Button size="large" variant="primary" loading={savingTaxInfo} onClick={handleSave}>
                        LƯU
                      </Button>
                    </InlineStack>
                  </BlockStack>
                )}

                {currentStep === 4 && (
                  <BlockStack gap="400">
                    {saveSuccessMessage ? <Banner tone="success">{saveSuccessMessage}</Banner> : null}

                    <Text as="p" variant="headingMd">
                      Bước 4: Hệ thống đã lưu thông tin thuế cho vé của bạn.
                    </Text>
                    <OrderSumary />
                    <Card>
                      <BlockStack gap="200">
                        <Text as="p" variant="headingMd">
                          Mã đơn: {orderCode}
                        </Text>
                        <Text as="p" variant="headingMd">
                          Loại thuế: {displayTaxInfo.tax_type === 'COMPANY' ? 'Doanh nghiệp' : 'Cá nhân'}
                        </Text>
                        <Text as="p" variant="headingMd">
                          Tên xuất hóa đơn: {displayTaxInfo.name || '—'}
                        </Text>
                        <Text as="p" variant="headingMd">
                          Mã quan hệ ngân sách: {displayTaxInfo.tax_mamoiquanhengansach || '—'}
                        </Text>
                        <Text as="p" variant="headingMd">
                          Địa chỉ: {displayTaxInfo.invoice_address || '—'}
                        </Text>
                        <Text as="p" variant="headingMd">
                          Email: {displayTaxInfo.invoice_email || '—'}
                        </Text>
                        <Text as="p" variant="headingMd">
                          Số điện thoại: {displayTaxInfo.invoice_phone || '—'}
                        </Text>
                      </BlockStack>
                    </Card>

                    <InlineStack align="end">
                      <Button
                        size="large"
                        onClick={() => {
                          setCurrentStep(1);
                          setOrderCodeInput('');
                          setOrderCode('');
                          setOrderId('');
                          setTicketError('');
                          setSaveError('');
                          setSaveSuccessMessage('');
                          setSavedTaxInfo(null);
                          setFormData({
                            invoice_tax_type: 'PERSONAL',
                            name: '',
                            buyer_name: '',
                            invoice_tax_code: '',
                            invoice_tax_cccd: '',
                            invoice_tax_mamoiquanhengansach: '',
                            invoice_email: '',
                            invoice_phone: '',
                            invoice_address: '',
                          });
                        }}
                      >
                        Điền vé khác
                      </Button>
                    </InlineStack>
                  </BlockStack>
                )}
              </BlockStack>
            </Card>
          </BlockStack>
        </Box>
      </div>

      <br />
      <FooterHelp align="start">
        <BlockStack gap="200">
          <Text as="p" fontWeight="bold">
            {settings?.site_name || '-'}
          </Text>
          <BlockStack>
            <Text as="p" tone="subdued">
              {settings?.site_main_address_1 || '-'}
            </Text>
            <Text as="p" tone="subdued">
              {settings?.site_main_address_2 || '-'}
            </Text>
            <Text as="p" tone="subdued">
              Hotline liên hệ: {settings?.company_phonenumber || '-'}
            </Text>
          </BlockStack>
        </BlockStack>
      </FooterHelp>
    </Page>
  );
}
